import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect } from "react";
import {
  FaArrowRight,
  FaMicrophone,
  FaRobot,
  FaChartLine,
  FaHeadset,
  FaBell,
  FaCheckCircle,
  FaClock,
  FaShieldAlt,
  FaStar,
  FaBolt,
} from "react-icons/fa";
import { BiBot } from "react-icons/bi";
import { MdOutlineCall, MdOutlineDashboard } from "react-icons/md";
import { FaArrowUpRightFromSquare } from "react-icons/fa6";
import "./products.css";

/* ─── PRODUCT DATA ────────────────────────────────────── */
const featuredProduct = {
  id: "swarix",
  name: "Ai Calling  Cloud Platform",
  tagline: "AI-powered voice agents that transform business communication",
  description:
    "Intelligent voice automation platform that handles calls, supports customers, and streamlines business communication 24/7.",
  longDescription:
    "Ai Calling combines cutting-edge voice AI with intelligent workflows to create natural, human-like conversations. From customer support to sales calls, our platform handles millions of interactions with zero compromise on quality.",
  icon: FaMicrophone,
  color: "#12AF09",
  features: [
    {
      icon: FaRobot,
      title: "AI Voice Agents",
      desc: "Natural-sounding AI agents that handle calls 24/7",
    },
    {
      icon: MdOutlineCall,
      title: "Call Automation",
      desc: "Automate inbound and outbound calls at scale",
    },
    {
      icon: BiBot,
      title: "Smart Workflows",
      desc: "Customizable conversation flows and routing",
    },
    {
      icon: MdOutlineDashboard,
      title: "Cloud Dashboard",
      desc: "Real-time monitoring and analytics",
    },
    {
      icon: FaChartLine,
      title: "Performance Analytics",
      desc: "Detailed insights on call metrics and ROI",
    },
    {
      icon: FaShieldAlt,
      title: "Enterprise Security",
      desc: "End-to-end encryption and compliance",
    },
  ],
  benefits: [
    "Reduce customer service costs by up to 60%",
    "Handle unlimited concurrent calls",
    "24/7 availability without breaks",
    "Instant scalability during peak times",
  ],
  metrics: [
    { value: "99.9%", label: "Uptime SLA" },
    { value: "<30-60ms", label: "Response Time" },
    { value: "1K+", label: "Daily Calls" },
    { value: "24/7", label: "Availability" },
  ],
  link: "",
  status: "live",
  category: "AI & Voice",
};

const caseStudies = [
  {
    company: "SupportFlow",
    industry: "Customer Service",
    result: "60% cost reduction",
    description:
      "Replaced 50 human agents with Ai Calling, handling 10,000+ daily calls",
    icon: FaHeadset,
  },
  {
    company: "MediCall",
    industry: "Healthcare",
    result: "24/7 patient support",
    description: "Automated appointment scheduling and prescription refills",
    icon: FaBell,
  },
  {
    company: "QuickShip",
    industry: "Logistics",
    result: "3x call capacity",
    description: "Peak season handling with zero dropped calls",
    icon: FaClock,
  },
];

/* ─── MOTION ───────────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.1 } } };
const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
};

/* ─── PAGE ─────────────────────────────────────────────── */
export default function ProductsPage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="products-page">
      {/* ═══ HERO - Minimal Tech ═══════════════════════════ */}
      <section className="products-hero" ref={heroRef}>
        <div className="products-hero-bg">
          <motion.div className="hero-grid-bg" style={{ y: bgY }}>
            {[...Array(20)].map((_, i) => (
              <div key={i} className="grid-line"></div>
            ))}
          </motion.div>
        </div>

        <div className="products-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="products-hero-tag"
          >
            <span className="hero-tag-line"></span>
            <span>OUR PRODUCTS</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="products-hero-title"
          >
            Powerful Software
            <br />
            <span>Built for Real-World Use</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="products-hero-desc"
          >
            We build scalable and high-performance software solutions for modern
            businesses. Here is one of our featured products, and more
            innovative solutions are coming soon.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="products-hero-stats"
          >
            <div className="hero-stat">
              <span className="hero-stat-value">1</span>
              <span className="hero-stat-label">Live Product</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-value">4+</span>
              <span className="hero-stat-label">In Development</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-value">5K+</span>
              <span className="hero-stat-label">Daily Users</span>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="hero-scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <span>Explore Products</span>
          <div className="scroll-line"></div>
        </motion.div>
      </section>

      {/* ═══ FEATURED PRODUCT - ═══════════════════ */}
      <section className="featured-product" id={featuredProduct.id}>
        <div className="featured-container">
          {/* Product header */}
          <motion.div
            className="featured-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="featured-status">
              <span className="status-dot"></span>
              <span>FEATURED PRODUCT — LIVE NOW</span>
            </div>
          </motion.div>

          <div className="featured-grid">
            {/* Left column - Product info */}
            <motion.div
              className="featured-info"
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              <motion.div variants={fadeUp} className="featured-icon-wrapper">
                <div
                  className="featured-icon"
                  style={{ background: `${featuredProduct.color}15` }}
                >
                  <featuredProduct.icon
                    style={{ color: featuredProduct.color }}
                  />
                </div>
              </motion.div>

              <motion.h2 variants={fadeUp} className="featured-name">
                {featuredProduct.name}
              </motion.h2>

              <motion.p variants={fadeUp} className="featured-tagline">
                {featuredProduct.tagline}
              </motion.p>

              <motion.p variants={fadeUp} className="featured-description">
                {featuredProduct.longDescription}
              </motion.p>

              {/* Feature list */}
              <motion.div variants={fadeUp} className="featured-features">
                {featuredProduct.features.map((feature, i) => (
                  <div key={i} className="feature-item">
                    <div className="feature-icon">
                      <feature.icon />
                    </div>
                    <div>
                      <h4>{feature.title}</h4>
                      <p>{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </motion.div>

              {/* Metrics */}
              <motion.div variants={fadeUp} className="featured-metrics">
                {featuredProduct.metrics.map((metric, i) => (
                  <div key={i} className="metric-box">
                    <span className="metric-value">{metric.value}</span>
                    <span className="metric-label">{metric.label}</span>
                  </div>
                ))}
              </motion.div>

              {/* CTA */}
              <motion.div variants={fadeUp} className="featured-actions">
                <a
                  href={featuredProduct.link}
                  className="featured-btn-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit Website <FaArrowUpRightFromSquare />
                </a>
                <a href="#contact" className="featured-btn-secondary">
                  Request Demo
                </a>
              </motion.div>
            </motion.div>

            {/* Right column - Visual showcase */}
            <motion.div
              className="featured-visual"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="visual-container">
                <div className="visual-card main-card">
                  <div className="card-header">
                    <div className="card-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <span>AI Callling Dashboard</span>
                  </div>
                  <div className="card-content">
                    <div className="waveform">
                      {[...Array(20)].map((_, i) => (
                        <div
                          key={i}
                          className="wave-bar"
                          style={{
                            height: `${Math.sin(i * 0.5) * 30 + 40}px`,
                            animationDelay: `${i * 0.05}s`,
                          }}
                        ></div>
                      ))}
                    </div>
                    <div className="stats-grid">
                      <div className="stat-item">
                        <span>Active Calls</span>
                        <strong>1,284</strong>
                      </div>
                      <div className="stat-item">
                        <span>Avg. Duration</span>
                        <strong>3:24</strong>
                      </div>
                      <div className="stat-item">
                        <span>Success Rate</span>
                        <strong>97%</strong>
                      </div>
                    </div>
                    <div className="activity-feed">
                      <div className="activity-item">
                        <span className="activity-dot"></span>
                        <span>Call completed - Support ticket #1284</span>
                      </div>
                      <div className="activity-item">
                        <span className="activity-dot"></span>
                        <span>New customer inquiry - Sales queue</span>
                      </div>
                      <div className="activity-item">
                        <span className="activity-dot"></span>
                        <span>Appointment scheduled - Dr. Smith</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="floating-badge badge-1">
                  <FaCheckCircle /> 99.9% Uptime
                </div>
                <div className="floating-badge badge-2">
                  <FaBolt /> Real-time AI
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ BENEFITS STRIP ════════════════════════════════ */}
      <section className="benefits-strip">
        <div className="benefits-container">
          {featuredProduct.benefits.map((benefit, i) => (
            <motion.div
              key={i}
              className="benefit-pill"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <FaCheckCircle className="benefit-icon" />
              <span>{benefit}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ═══ CASE STUDIES ══════════════════════════════════ */}
      <section className="case-studies">
        <div className="case-studies-header">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="section-tag"
          >
            <span>REAL RESULTS</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Trusted by <span>innovative companies</span>
          </motion.h2>
        </div>

        <div className="case-grid">
          {caseStudies.map((study, i) => (
            <motion.div
              key={i}
              className="case-card"
              variants={scaleIn}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              whileHover={{ y: -8 }}
            >
              <div className="case-icon">
                <study.icon />
              </div>
              <div className="case-content">
                <span className="case-industry">{study.industry}</span>
                <h3>{study.company}</h3>
                <p>{study.description}</p>
                <div className="case-result">
                  <FaStar className="result-icon" />
                  <span>{study.result}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ═══ ROADMAP PREVIEW ═══════════════════════════════ */}
      <section className="roadmap-preview">
        <div className="roadmap-container">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Product <span>roadmap</span>
          </motion.h2>

          <div className="roadmap-timeline">
            <motion.div
              className="roadmap-item"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div
                className="roadmap-dot"
                style={{ background: featuredProduct.color }}
              ></div>
              <div className="roadmap-content">
                <span className="roadmap-date">Now</span>
                <h4>{featuredProduct.name}</h4>
                <p>Live and production-ready</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ WAITING LIST / CTA ════════════════════════════ */}
      <section className="products-cta">
        <div className="cta-card">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>
              Be the first to know when
              <br />
              <span>new products launch</span>
            </h2>
            <p>
              Join our waiting list for early access, beta invitations, and
              exclusive updates on upcoming releases.
            </p>
            <form className="waitlist-form">
              <input type="email" placeholder="Enter your email" />
              <button type="submit">
                Notify me <FaArrowRight />
              </button>
            </form>
            <p className="form-note">No spam. Unsubscribe anytime.</p>
          </motion.div>
          <div className="cta-glow"></div>
        </div>
      </section>
    </div>
  );
}
